import type { Database } from 'node-sqlite3-wasm';
import { listImportsForFile, type DbImportRow } from '../../symbol-index/sqlite/symbol-index-import-sqlite.js';
import { getSymbolIndexParentContextById } from '../../symbol-index/sqlite/symbol-index-sqlite.js';
import {
  listEffectiveIndexedFilePaths,
  loadFileImports,
  loadFileTags,
} from '../../symbol-index/storage/symbol-parent-file-map.js';
import type { DbTagRow } from '../types.js';

export type NavigationLookups = {
  loadFileDefs: (filePath: string) => readonly DbTagRow[];
  lookupDefsInFileNamed: (filePath: string, name: string) => readonly DbTagRow[];
  lookupImportsForFile: (filePath: string) => readonly DbImportRow[];
};

function defsByFileNameKey(filePath: string, name: string): string {
  return `${filePath}\0${name}`;
}

/** Cached SQLite lookups for navigation — avoids loading the full workspace def/import tables. */
export function createNavigationLookups(db: Database, parentId: number): NavigationLookups {
  const ctx = getSymbolIndexParentContextById(db, parentId);
  if (ctx == null) {
    return {
      loadFileDefs: () => [],
      lookupDefsInFileNamed: () => [],
      lookupImportsForFile: () => [],
    };
  }

  const fileDefsCache = new Map<string, DbTagRow[]>();
  const defsByFileNameCache = new Map<string, DbTagRow[]>();
  const importsCache = new Map<string, DbImportRow[]>();

  return {
    loadFileDefs(filePath: string): readonly DbTagRow[] {
      let cached = fileDefsCache.get(filePath);
      if (cached) return cached;
      cached = loadFileTags(db, ctx, filePath).filter((row) => row.role === 'def');
      fileDefsCache.set(filePath, cached);
      return cached;
    },

    lookupDefsInFileNamed(filePath: string, name: string): readonly DbTagRow[] {
      const key = defsByFileNameKey(filePath, name);
      let cached = defsByFileNameCache.get(key);
      if (cached) return cached;
      cached = loadFileTags(db, ctx, filePath).filter((row) => row.role === 'def' && row.name === name);
      defsByFileNameCache.set(key, cached);
      return cached;
    },

    lookupImportsForFile(filePath: string): readonly DbImportRow[] {
      let cached = importsCache.get(filePath);
      if (cached) return cached;
      cached = listImportsForFile(db, parentId, filePath);
      importsCache.set(filePath, cached);
      return cached;
    },
  };
}

export function createSnapshotNavigationLookups(db: Database, parentId: number): NavigationLookups {
  const ctx = getSymbolIndexParentContextById(db, parentId);
  if (ctx == null) {
    return {
      loadFileDefs: () => [],
      lookupDefsInFileNamed: () => [],
      lookupImportsForFile: () => [],
    };
  }

  const fileDefsCache = new Map<string, DbTagRow[]>();
  const defsByFileNameCache = new Map<string, DbTagRow[]>();
  const importsCache = new Map<string, DbImportRow[]>();

  for (const filePath of listEffectiveIndexedFilePaths(db, ctx)) {
    const defs = loadFileTags(db, ctx, filePath).filter((row) => row.role === 'def');
    fileDefsCache.set(filePath, defs);
    for (const def of defs) {
      const key = defsByFileNameKey(filePath, def.name);
      const existing = defsByFileNameCache.get(key);
      if (existing) {
        existing.push(def);
      } else {
        defsByFileNameCache.set(key, [def]);
      }
    }
    importsCache.set(filePath, loadFileImports(db, ctx, filePath));
  }

  return {
    loadFileDefs: (filePath: string) => fileDefsCache.get(filePath) ?? [],
    lookupDefsInFileNamed: (filePath: string, name: string) =>
      defsByFileNameCache.get(defsByFileNameKey(filePath, name)) ?? [],
    lookupImportsForFile: (filePath: string) => importsCache.get(filePath) ?? [],
  };
}
